import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/common/apiClient";
import { toast } from "sonner";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";

interface KPIForm {
  _id: string;
  title: string;
  description: string;
  status: string;
}

interface UserKPIForm {
  _id: string;
  kpiFormId: string;
  status: string;
}

export const FormStartPage = () => {
  const navigate = useNavigate();
  const [kpiForms, setKpiForms] = useState<KPIForm[]>([]);
  const [userForms, setUserForms] = useState<UserKPIForm[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [startingFormId, setStartingFormId] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [kpiFormsRes, userFormsRes] = await Promise.all([
        apiClient.get("/api/kpi-form"),
        apiClient.get("/api/user-kpi-form/my-forms"),
      ]);

      if (kpiFormsRes.success) {
        const published = (kpiFormsRes.data || []).filter(
          (f: KPIForm) => f.status === "published"
        );
        setKpiForms(published);
      }

      if (userFormsRes.success) {
        setUserForms(userFormsRes.data || []);
      }
    } catch (error) {
      toast.error("Failed to load forms");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStart = async (kpiFormId: string) => {
    setStartingFormId(kpiFormId);
    try {
      const response = await apiClient.post("/api/user-kpi-form/start", {
        kpiFormId,
      });

      if (response.success) {
        toast.success("Form started successfully");
        navigate(`/user-kpi-form/${response.data._id || response.data.id}`);
      } else {
        toast.error("Failed to start form");
      }
    } catch (error) {
      toast.error("Failed to start form");
    } finally {
      setStartingFormId(null);
    }
  };

  const getUserForm = (kpiFormId: string) => {
    return userForms.find((uf) => uf.kpiFormId === kpiFormId);
  };

  const handleContinue = (userFormId: string) => {
    navigate(`/user-kpi-form/${userFormId}`);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">My KPI Forms</h1>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {kpiForms.map((form) => (
            <TableRow key={form._id}>
              <TableCell>{form.title}</TableCell>
              <TableCell>{form.description || "-"}</TableCell>
              <TableCell>
                {hasUserForm(form._id) ? (
                  <span className="text-sm text-gray-500">Started</span>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => handleStart(form._id)}
                    disabled={startingFormId === form._id}
                  >
                    {startingFormId === form._id ? "Starting..." : "Start"}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
